import { Helmet } from 'react-helmet-async';

const DEFAULT_TITLE = 'CampYid — Camp Vendor Directory';
const DEFAULT_DESCRIPTION = 'Find trusted camp vendors for entertainment, catering, transportation, and more. CampYid connects summer camps with top service providers.';
const DEFAULT_IMAGE = 'https://campyid.com/icons/icon-512x512.png';
const SITE_URL = 'https://campyid.com';

/**
 * SEO Component for dynamic meta tags
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.image - Open Graph image URL
 * @param {string} props.url - Canonical URL path (e.g., "/listing/vendor-name")
 * @param {string} props.type - Open Graph type (default: "website")
 * @param {Object} props.structuredData - JSON-LD structured data object
 */
export const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  structuredData,
  noIndex = false
}) => {
  const fullTitle = title ? `${title} | CampYid` : DEFAULT_TITLE;
  const metaDescription = description || DEFAULT_DESCRIPTION;
  const metaImage = image || DEFAULT_IMAGE;
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="CampYid" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      
      {/* Structured Data / JSON-LD */}
      {structuredData && (
        Array.isArray(structuredData) 
          ? structuredData.map((data, i) => (
              <script key={`schema-${i}`} type="application/ld+json">
                {JSON.stringify(data)}
              </script>
            ))
          : <script type="application/ld+json">
              {JSON.stringify(structuredData)}
            </script>
      )}
    </Helmet>
  );
};

/**
 * Generate Organization structured data for homepage
 */
export const generateOrganizationSchema = () => ([
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "CampYid",
    "url": SITE_URL,
    "logo": `${SITE_URL}/icons/icon-512x512.png`,
    "description": "CampYid is the premier camp vendor directory connecting summer camps with trusted service providers for entertainment, catering, transportation, and more.",
    "sameAs": []
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "CampYid",
    "url": SITE_URL,
    "description": "Camp vendor directory for summer camps",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is a camp vendor?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A camp vendor is a business that provides products or services to summer camps. This includes entertainment performers, kosher food and catering companies, transportation services, suppliers of equipment and gear, and specialty program providers. Jewish camp vendors typically offer services tailored to the needs of frum and Orthodox summer camps."
        }
      },
      {
        "@type": "Question",
        "name": "How do I find kosher catering and entertainment for my summer camp?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "CampYid is a free camp vendor directory where camp directors can browse kosher catering services, Jewish entertainment performers, and all other camp vendor categories. Search by category, location, or service type to find kosher-friendly vendors experienced with summer camps."
        }
      },
      {
        "@type": "Question",
        "name": "How do camp vendors list their business on CampYid?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Camp vendors can create a listing on CampYid by signing up, adding their business details, uploading photos and videos, selecting service categories like entertainment, food & catering, transportation, or supplies, and publishing their profile. Camp directors can then discover and contact them directly."
        }
      },
      {
        "@type": "Question",
        "name": "What categories of camp vendors are available on CampYid?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "CampYid features camp vendors in 10 categories: Entertainment (performers, DJs, shows), Trips & Excursions, Food & Catering (kosher options), Health & Safety, Transportation, Office & Admin, Supplies & Equipment, Promos & Swag, Services & Other, and Specialty Programs (Special ED, arts, learning)."
        }
      }
    ]
  }
]);

/**
 * Generate LocalBusiness structured data for vendor listings
 */
export const generateListingSchema = (listing) => {
  if (!listing) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": listing.title,
    "description": listing.description?.substring(0, 200),
    "url": `${SITE_URL}/listing/${listing.slug}`,
  };
  
  if (listing.flyer_image) {
    schema.image = listing.flyer_image;
  }
  
  if (listing.contact_email) {
    schema.email = listing.contact_email;
  }
  
  if (listing.contact_phone) {
    schema.telephone = listing.contact_phone;
  }
  
  if (listing.website) {
    schema.sameAs = [listing.website];
  }
  
  if (listing.service_areas?.length > 0) {
    schema.areaServed = listing.service_areas.map(area => ({
      "@type": "Place",
      "name": area
    }));
  }
  
  return schema;
};

/**
 * Generate ItemList structured data for category pages
 */
export const generateCategorySchema = (category, listings = []) => {
  if (!category) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${category.name} Vendors for Summer Camps`,
    "description": category.description || `Browse ${category.name} vendors for your summer camp`,
    "url": `${SITE_URL}/category/${category.slug}`,
    "numberOfItems": listings.length,
    "itemListElement": listings.slice(0, 10).map((listing, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "LocalBusiness",
        "name": listing.title,
        "url": `${SITE_URL}/listing/${listing.slug}`
      }
    }))
  };
};

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url ? `${SITE_URL}${item.url}` : undefined
  }))
});

export default SEO;
